import { useEffect, useMemo, useState } from 'react';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

// Global storage for the deferred prompt - set up early before React loads
// This ensures we capture the event even if it fires before components mount
let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
let globalPromptListeners: Set<(prompt: BeforeInstallPromptEvent | null) => void> = new Set();

// Set up the event listener immediately when this module loads
if (typeof window !== 'undefined') {
  const onBeforeInstallPrompt = (e: Event) => {
    // Prevent the default mini-infobar so we can show a custom banner
    (e as any).preventDefault?.();
    const prompt = e as BeforeInstallPromptEvent;
    globalDeferredPrompt = prompt;
    // Notify all listeners
    globalPromptListeners.forEach(listener => listener(prompt));
  };

  const onAppInstalled = () => {
    globalDeferredPrompt = null;
    // Notify all listeners
    globalPromptListeners.forEach(listener => listener(null));
  };

  // Attach listeners immediately - this runs when the module is first imported
  window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt as any);
  window.addEventListener('appinstalled', onAppInstalled);
}

const isRunningAsPwa = (): boolean => {
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches === true ||
    // iOS Safari "Add to Home Screen"
    (window.navigator as any).standalone === true
  );
};

const isIosSafari = (): boolean => {
  const ua = navigator.userAgent;
  const isIOS = /iPad|iPhone|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return isIOS && isSafari;
};

export function usePwaInstallPrompt() {
  // Initialize with the global prompt if it already exists
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() => globalDeferredPrompt);
  const [isInstalled, setIsInstalled] = useState<boolean>(() => isRunningAsPwa());

  const iosSafari = useMemo(() => isIosSafari(), []);
  const runningAsPwa = useMemo(() => isRunningAsPwa(), [isInstalled]);

  useEffect(() => {
    // Set up listener to be notified when the global prompt changes
    const listener = (prompt: BeforeInstallPromptEvent | null) => {
      setDeferredPrompt(prompt);
    };
    
    globalPromptListeners.add(listener);
    
    // Also check if we already have a prompt (in case it was set before this component mounted)
    if (globalDeferredPrompt) {
      setDeferredPrompt(globalDeferredPrompt);
    }

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      globalPromptListeners.delete(listener);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []); // Empty deps - only run once on mount

  const canPromptInstall = !!deferredPrompt;

  const promptInstall = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    if (!deferredPrompt) return 'unavailable';
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice.catch(() => null);
      // Clear the global prompt after use
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
      if (!choice) return 'dismissed';
      return choice.outcome;
    } catch (error) {
      console.error('Error calling install prompt:', error);
      // Clear on error
      globalDeferredPrompt = null;
      setDeferredPrompt(null);
      return 'unavailable';
    }
  };

  return {
    runningAsPwa,
    iosSafari,
    canPromptInstall,
    promptInstall,
  };
}

